import { ColumnType, RowType, useTable, CellType } from 'react-final-table';
import { NavLink, useLocation } from 'react-router-dom';
import CheckboxInput from '../checkbox/checkbox';
import IconDropdownUp from '../icons/dropdown-up';
import IconDropdownDown from '../icons/dropdown-down';
import IconLoader from '../icons/loader';
import ButtonSecondary from '../button/button-secondary';
import { ButtonSize } from '../button/types';
import { TransactionTypeEnum } from '../transactions-sidepanels/types';
import { TableRowProps, Row } from './types';
import IconCopy from '../icons/copy';
import IconTrash from '../icons/trash';

import './table.css';
import formatDate from '../../helpers/format-date';
import ToggleInput from '../checkbox/toggle';

const ExpandableRows = ({
  expandableColumns,
  values,
}: {
  expandableColumns: ColumnType<Record<string, unknown>>[];
  values: Record<any, any>[];
}): React.JSX.Element => {
  const { rows } = useTable(expandableColumns, values);

  const { search } = useLocation();

  return (
    <>
      {rows.map((item, index) => (
        <tr
          key={item.original.id || `row-${item.id}`}
          className="expanded-row-wrapper"
        >
          <td className="expandable-сell" aria-label="expandable-сell" />
          {item.cells.map((cell) => (
            <td
              key={`cell-${(cell as CellType & { field: string }).field}`}
              className="table-body-cell"
            >
              <div className="table-body-content-wrapper">
                {values[index]?.render &&
                (values[index] as { render: Record<string, React.JSX.Element> })
                  ?.render[(cell as CellType & { field: string }).field]
                  ? (
                      values[index] as {
                        render: Record<string, React.JSX.Element>;
                      }
                    )?.render[(cell as CellType & { field: string }).field]
                  : cell.render()}
                {values[index]?.url && (
                  <NavLink
                    to={`${values[index]?.url}${search || ''}`}
                    className="row-link"
                  />
                )}
              </div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

export default function TableRow({
  expandable,
  row,
  isLoading,
  onRowsSelect,
  selectRow,
  expandableColumns,
  onExpand,
  expandedRows,
  data,
  selected,
  isLoadingExpandableRow,
  expandableColspan,
  disabledRowIds,
  clickable,
  linkWithoutSearch,
  onRowClick,
  withDuplicateButton,
  onDuplicateClick,
  transactionTable,
  hasDeleted,
  withExpandableContent,
  expandableColspanValue,
  withToggleInput,
  tableName,
  newWindow,
  rowsSelectDisabled,
  withDeleteIcon,
}: TableRowProps): React.JSX.Element {
  const { search } = useLocation();

  const valuesKey = Object.keys(row.original).find((key) =>
    Array.isArray(row.original[key as keyof RowType<Record<any, any>>]),
  );
  const values = row.original[valuesKey as string];

  let colSpan;

  if (expandableColspanValue && withExpandableContent) {
    colSpan = expandableColspanValue;
  } else {
    colSpan = expandableColspanValue || expandableColspan;
  }

  return (
    <>
      <tr
        className={`${isLoading ? 'loading' : ''} ${
          expandedRows?.length > 0 ? 'low-opacity' : ''
        } ${
          expandedRows.find((item) => item === row.original.id.toString())
            ? 'expanded'
            : ''
        } ${clickable || onRowClick ? 'clickable' : ''} row-wrapper`}
        onClick={() => onRowClick && onRowClick(row.original.id)}
        id={row.original.id}
      >
        {expandable && (
          <td
            className={`expandable-cell table-body-cell ${
              transactionTable && row.original.deletedAt
                ? 'table-body-cell-red'
                : ''
            } ${hasDeleted && !withDeleteIcon ? 'more-padding' : ''}`}
          >
            {transactionTable && row.original.deletedAt && !withDeleteIcon && (
              <div
                className="delete-icon-wrapper"
                data-text={`Deleted ${formatDate(
                  row.original.deletedAt || '',
                  true,
                )}`}
              >
                <IconTrash className="deleted-icon" width={18} height={18} />
              </div>
            )}
            {!(
              row.original.type &&
              row.original.type === TransactionTypeEnum.GENERAL_LEDGER
            ) && (
              <button
                type="button"
                aria-label="Expand Button"
                className="expand-button"
                onClick={() => {
                  onExpand(row.id.toString(), row.original.id);
                }}
                tabIndex={-1}
              >
                {expandedRows.includes(row.original.id.toString()) ? (
                  <IconDropdownUp />
                ) : (
                  <IconDropdownDown />
                )}
              </button>
            )}
          </td>
        )}
        {Boolean(onRowsSelect) && (
          <td
            className={`table-body-cell ${
              transactionTable && row.original.deletedAt
                ? 'table-body-cell-red'
                : ''
            } ${hasDeleted && !withDeleteIcon && !expandable ? 'more-padding' : ''}`}
          >
            {!expandable &&
              transactionTable &&
              row.original.deletedAt &&
              !withDeleteIcon && (
                <div
                  className="delete-icon-wrapper"
                  data-text={`Deleted ${formatDate(
                    row.original.deletedAt || '',
                    true,
                  )}`}
                >
                  <IconTrash className="deleted-icon" width={18} height={18} />
                </div>
              )}
            {withToggleInput ? (
              <ToggleInput
                name={`checkbox-${row.original.id as string}${tableName ? `-${tableName}` : ''}`}
                title=""
                checked={selected}
                onChange={() => selectRow(row as Row)}
                tabIndex={-1}
                disabled={
                  disabledRowIds?.includes(row.original.id) ||
                  rowsSelectDisabled
                }
              />
            ) : (
              <CheckboxInput
                type="checkbox"
                name={`checkbox-${row.original.id as string}${tableName ? `-${tableName}` : ''}`}
                title=""
                checked={selected}
                onChange={() => selectRow(row as Row)}
                tabIndex={-1}
                disabled={
                  disabledRowIds?.includes(row.original.id) ||
                  rowsSelectDisabled
                }
              />
            )}
          </td>
        )}
        {data &&
          row.cells.map((cell, index) => (
            <td
              key={`cell-${(cell as CellType & { field: string }).field}`}
              className={`table-body-cell ${
                transactionTable && row.original.deletedAt
                  ? 'table-body-cell-red'
                  : ''
              } ${
                hasDeleted && !expandable && !onRowsSelect && index === 0
                  ? 'more-padding'
                  : ''
              }`}
            >
              {!expandable &&
                !onRowsSelect &&
                transactionTable &&
                row.original.deletedAt &&
                index === 0 && (
                  <div
                    className="delete-icon-wrapper"
                    data-text={`Deleted ${formatDate(
                      row.original.deletedAt || '',
                      true,
                    )}`}
                  >
                    <IconTrash
                      className="deleted-icon"
                      width={18}
                      height={18}
                    />
                  </div>
                )}
              <div className="table-body-content-wrapper">
                {data?.render &&
                (data as { render: Record<string, React.JSX.Element> })?.render[
                  (cell as CellType & { field: string }).field
                ]
                  ? (data as { render: Record<string, React.JSX.Element> })
                      ?.render[(cell as CellType & { field: string }).field]
                  : cell.render()}
                {clickable && (
                  <NavLink
                    to={`${data?.urlList || data?.url}${
                      !linkWithoutSearch ? search || '' : ''
                    }`}
                    className="row-link"
                    target={newWindow ? '_blank' : '_self'}
                  />
                )}
                {withDuplicateButton && onDuplicateClick && index === 0 && (
                  <ButtonSecondary
                    size={ButtonSize.SMALL}
                    className="button-white duplicate-button"
                    icon={<IconCopy />}
                    onClick={() => onDuplicateClick(row.original.id)}
                  />
                )}
              </div>
            </td>
          ))}
      </tr>
      {expandedRows.find((item) => item === row.original.id.toString()) && (
        <tr className="expanded-table-row">
          <td colSpan={colSpan} className="expanded-table-body-cell">
            {!withExpandableContent && (
              <table>
                <thead>
                  <tr>
                    <th
                      className="expandable-header"
                      aria-label="Expandable Header"
                    />
                    {expandableColumns?.map((item) => (
                      <th key={item.name}>{item.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoadingExpandableRow && (
                    <tr className="expanded-table-loader-wrapper">
                      <td colSpan={100} aria-label="Loader wrapper">
                        <IconLoader />
                      </td>
                    </tr>
                  )}
                  {!isLoadingExpandableRow && !values.length && (
                    <tr className="expanded-table-empty-message">
                      <td colSpan={100}>Data not found</td>
                    </tr>
                  )}
                  {!isLoadingExpandableRow && values.length > 0 && (
                    <ExpandableRows
                      expandableColumns={
                        expandableColumns as ColumnType<
                          Record<string, unknown>
                        >[]
                      }
                      values={values}
                    />
                  )}
                </tbody>
              </table>
            )}
            {withExpandableContent &&
              data?.render &&
              data?.render?.values &&
              data.render.values}
          </td>
          <td
            colSpan={100}
            className="expanded-table-body-cell"
            aria-label="expanded-cell"
          />
        </tr>
      )}
    </>
  );
}
